/**
 * Copyright IBM Corp. 2022
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import Hammer from "hammerjs";
import throttle from "lodash/throttle";

import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanAPI from "../api/TriFloorPlanAPI.js";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";
import dragIcon from "../../../images/draggable.svg";
import zoomBoxIcon from "../../../images/zoom--in-area.svg";
import zoomBoxIconSelected from "../../../images/zoom--in-area-selected.svg";
import zoomInIcon from "../../../images/zoom--in.svg";
import zoomOutIcon from "../../../images/zoom--out.svg";
import zoomFitIcon from "../../../images/zoom--fit.svg";
import TriPanPlugin from "./TriPanPlugin.js";

export default class TriNavPlugin extends TriFloorPlanPlugin {
    // Definitions:
    // Nav Window: The top level window for the entire control
    // Client:    The window in which the full sized floorplan is displayed.
    //            created by TriFloorPlan.js
    // Thumbnail: A smaller window with the same aspect ration as the client
    //            that has a small scale copy of the florplan used to show
    //            and set the floorplan position and scale in the client.
    //            The small scale floorplan in the thumbnail is always
    //            scale 1 and centered.
    // Viewport   A scale window within the thumbnail that shows the relationship
    //            of the client to the floorplan rendered in it.  It's size and
    //            position changes to reflect pan and zoom of the floorplan.
    //            It can also be directly manipulated to pan and zoom the
    //            floorplan
    // Shadow:    A duplicate copy of the SVG exactly the same size and in exactly
    //            the same screen position as the client SVG.  It is never panned
    //            or zoomed.  It is used to recacluate the initial state if the
    //            window is resized while the client is zoomed or panned

    static type = "nav";

    constructor() {
        super();
        this.thumbnail = {};
        this.shadow = {};
        this.started = false;
        this.size = 100;
        this.maxScale = 10;
        this.minScale = 0.25;
        this.minHeight = 250;
        this.minWidth = 250;
        this.isZoomBoxActive = false;

        this.handleZoomBoxDragEnd = this.handleZoomBoxDragEnd.bind( this );
    }

    set enableZoomBox( state )
    {
        if( this.zoomBox ) this.zoomBox.disabled = !state;
    }

    pluginDidUpdate()
    {
        const { triFloorPlan, floorPlanLoaded, enabled } = this.props;
        const { svgElement } = triFloorPlan;

        if( !floorPlanLoaded || !enabled || !svgElement )
        {
            this.stop();
            return;
        }

        this.triFloorPlan = triFloorPlan;
        this.start();

        if( this.theme !== this.props.theme )
        {
            this.theme = this.props.theme;
            this.setStyle();
        }
    }

    pluginDidRemove()
    {
        this.stop();
    }

    floorPlanDidResize()
    {
        this.sizeShadow();
        this.sizeNavWindow();
        this.initThumbnail();
        this.sizeNavWindow();
        this.handleNavResetPosition();
    }

    floorPlanDidPanOrZoom( svgViewBox )
    {
      // This needs to be async so the svgElement can render the new position
      // when the viewBox is set programatically.
      setTimeout( () =>
      {
        this.sizeAndPositionViewport( svgViewBox );
      }, 10 );
    }

    start()
    {
        if( this.started ) return;

        switch( this.props.size )
        {
            case "sm":
                this.size = 75;
                break;
            case "md":
                this.size = 100;
                break;
            case "lg":
                this.size = 150;
                break;
        }

        if( this.props.minDisplayHeight )
        {
            this.minHeight = this.props.minDisplayHeight;
        }
        if( this.props.minDisplayWidth )
        {
            this.minWidth = this.props.minDisplayWidth;
        }

        const { svgElement, floorPlanId } = this.triFloorPlan;

        this.started = true;

        this.zoomPlugin = null;
        this.triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === "zoom" ) this.zoomPlugin = instance;
        });

        this.navWindow = document.createElement( "div" );
        this.navWindow.className = "TriNavWindow";

        if( this.zoomPlugin !== null )
        {
            this.zoomBar = this.createZoomSlider()
            this.maxScale = this.zoomPlugin.maxScale;
            this.minScale = this.zoomPlugin.minScale;
            this.navWindow.appendChild( this.zoomBar );
        }

        const thumbBar = document.createElement( "div" );
        thumbBar.className = "TriNavThumbBar";

        thumbBar.appendChild( this.createControlPalette() );

        this.thumbnail.window = document.createElement("div");
        this.thumbnail.window.className = "TriNavThumbnail";
        this.thumbnail.window.addEventListener('dblclick', (evt) => this.handleZoomToFit(evt), true);
        thumbBar.appendChild( this.thumbnail.window );

        this.navWindow.appendChild( thumbBar );

        const container = svgElement.parentElement;
        container.appendChild(this.navWindow);

        this.setStyle();

        const floorPlan = this.getFloorPlan( floorPlanId );
        floorPlan.then( ( svg ) =>
        {
            this.thumbnail.window.innerHTML = svg

            // Add after innerHTML is set
            setTimeout( () =>
            {
                this.thumbnail.SVG = this.thumbnail.window.querySelector( "svg" );
                this.thumbnail.SVG.setAttribute( "preserveAspectRatio", "xMidYMid" );
                if( this.props.svgAriaLabel )
                {
                    this.thumbnail.SVG.setAttribute( "aria-label", this.props.svgAriaLabel );
                }
                this.createViewPort();
                this.sizeNavWindow();
            }, 1 );
        });

        this.createShadowSVG();
    }

    stop()
    {
        if( this.props.floorPlanLoaded )
        {
            if( this.navWindow )
            {
              this.navWindow.parentElement.removeChild( this.navWindow );
            }
            if( this.shadow.window )
            {
                this.shadow.window.parentElement.removeChild( this.shadow.window );
            }
        }
        this.navWindow = null;
        this.thumbnail = {};
        this.shadow = {}
        this.started = false;
    }

    async getFloorPlan( floorPlanId )
    {
        const floorPlan = await TriFloorPlanAPI.getFloorplan( floorPlanId );
        return floorPlan;
    }

    setStyle()
    {
        if( this.theme === "dark" )
        {
            this.thumbnail.window.style.backgroundColor = "black";
            this.dragHandle.style.color = "white";
            this.dragHandle.style.backgroundColor = "#393939";

            if( this.zoom )
            {
                this.zoomOutImg.style.color = "white";
                this.zoomOutImg.style.backgroundColor = "#393939";
                this.zoom.style.backgroundColor = "#393939";
                this.zoom.style.color = "white";
                this.zoom.style.backgroundColor = "#393939";
                this.zoomInImg.style.color = "white";
                this.zoomInImg.style.backgroundColor = "#393939";
                this.zoomFit.style.color = "white";
                this.zoomFit.style.backgroundColor = "#393939";
                this.zoomBox.style.color = "white";
                this.zoomBox.style.backgroundColor = "#393939";
            }
        }
        else
        {
            this.thumbnail.window.style.backgroundColor = "white";
            this.dragHandle.style.color = "black";
            this.dragHandle.style.backgroundColor = "white";

            if( this.zoom )
            {
                this.zoomOutImg.style.color = "black";
                this.zoomOutImg.style.backgroundColor = "white";
                this.zoom.style.color = "black";
                this.zoom.style.backgroundColor = "white";
                this.zoomInImg.style.color = "black";
                this.zoomInImg.style.backgroundColor = "white";
                this.zoomFit.style.color = "black";
                this.zoomFit.style.backgroundColor = "white";
                this.zoomBox.style.color = "black";
                this.zoomBox.style.backgroundColor = "white";
            }
        }  
    }

    /**
     * Bar on top of control with-zoom out button, zoom slider, and zoom-in button
     */
    createZoomSlider()
    {
        const zoomBar = document.createElement( "div" );
        zoomBar.className = "TriNavZoomBar";

        this.zoomOutImg = document.createElement( "img" );
        this.zoomOutImg.src = zoomOutIcon;
        this.zoomOutImg.width = 24;
        this.zoomOutImg.heiht = 24;
        this.zoomOutImg.addEventListener( 'click', () => { --this.zoom.value; this.handleZoom(); }, true );
        zoomBar.appendChild( this.zoomOutImg );

        this.zoom = document.createElement( "input" );
        this.zoom.className = "TriNavZoom";
        this.zoom.type = "range";
        this.zoom.min = 1;
        this.zoom.max = this.maxScale + 1 / this.minScale - 1;
        this.zoom.step = 1/ (( this.zoom.max - 1 ) * 3 );
        this.zoom.oninput = ( evt ) => this.handleZoom( evt );
        zoomBar.appendChild( this.zoom );

        this.zoomInImg = document.createElement( "img" );
        this.zoomInImg.src = zoomInIcon;
        this.zoomInImg.width = 24;
        this.zoomInImg.heiht = 24;
        this.zoomInImg.addEventListener( 'click', () => { ++this.zoom.value; this.handleZoom(); }, true );
        zoomBar.appendChild( this.zoomInImg );

        return zoomBar;
    }

    /*
     * Control palette on left side of control with move and zoom-to-fit controls
     */ 
    createControlPalette()
    {
        const { svgElement } = this.triFloorPlan;
        const container = svgElement.parentElement;

        const controlBar = document.createElement( "div" );
        controlBar.className = "TriNavControlBar";

        // Handle to drag/move thumbnail navigation control
        this.dragHandle = document.createElement( "div" );
        this.dragHandle.className = "TriNavDrag";
        this.dragHandle.style.backgroundImage = "url('" + dragIcon + "')";

        this.dragHandle.addEventListener( 'mousedown', ( evt ) => this.handleStartDragNav( evt ), true );
        container.addEventListener( 'mousemove', ( evt ) => this.handleDragNav( evt ), true );
        container.addEventListener( 'mouseup', () => { this.isDraggingNav = false }, true );
        this.dragHandle.addEventListener('mouseup', () => { this.isDraggingNav = false }, true);

        this.dragHandle.addEventListener('dblclick', (evt) => this.handleNavResetPosition(evt), true);

        controlBar.appendChild(this.dragHandle);

        this.zoomFit = document.createElement( "div" );
        this.zoomFit.className = "TriNavControl";
        this.zoomFitImg = document.createElement( "img" );
        this.zoomFitImg.src = zoomFitIcon;
        this.zoomFitImg.width = 24;
        this.zoomFitImg.heiht = 24;
        this.zoomFitImg.addEventListener( 'click', () => { this.handleZoomToFit(); }, true );
        this.zoomFit.appendChild( this.zoomFitImg );

        controlBar.appendChild( this.zoomFit );

        if( this.zoomPlugin !== null )
        {
            this.zoomBox = document.createElement( "div" );
            this.zoomBox.className = "TriNavControl";
            this.zoomBoxImg = document.createElement( "img" );
            this.zoomBoxImg.src = zoomBoxIcon;
            this.zoomBoxImg.width = 24;
            this.zoomBoxImg.height = 24;
            this.zoomBoxImg.addEventListener( 'click', () => { this.handleZoomBox(); }, true );
            this.zoomBox.appendChild( this.zoomBoxImg );

            controlBar.appendChild( this.zoomBox );
        }

        return controlBar;
    }

    /*
     * Shadow copy of SVG tha never zooms and pans.  Used to reestablish transform matricies
     * when the client window is re-sized. This is required because if the client is panned
     * or zoomed it can't be used to create the transfom maticies to map to the thumbnail SVG
     */ 
    createShadowSVG()
    {
        const { svgElement, floorPlanId, svgViewPort } = this.triFloorPlan;

        // This is a hack to detect that the floorplan has been programtically re-sized after it is
        // created.  The resize event only fires if the window is resized so progrmatic changes to
        // the enclosing DOM that causes the floorplan to resize are not detected and the svgViewPort
        // gets out of sync with the svgElement size.
        //
        // The right solution is to re-write the floorplan so it is contained in it's own iFrame which
        // will generate resize events.
        if( svgElement.clientWidth !== svgViewPort.width || svgElement.clientHeight !== svgViewPort.height )
        {
            this.triFloorPlan.floorPlanDidResize();
        }

        this.shadow.window = document.createElement( "div" );
        this.shadow.window.className = "TriNavShadow";
        const container = svgElement.parentElement;
        container.appendChild(this.shadow.window);

        const floorPlan = this.getFloorPlan(floorPlanId);
        floorPlan.then((svg) => {
        this.shadow.window.innerHTML = svg

        // Add after innerHTML is set
        setTimeout(() => {
            this.shadow.SVG = this.shadow.window.querySelector("svg");
            this.shadow.SVG.setAttribute("preserveAspectRatio", "xMidYMid");
            if( this.props.svgAriaLabel )
            {
                this.shadow.SVG.setAttribute("aria-label", this.props.svgAriaLabel);
            }

            TriFloorPlanUtils.setSvgViewBox(this.shadow.SVG, 0, 0, 0, 0);

            this.sizeShadow();
            this.initThumbnail();
            }, 1);
       });
    }

    createViewPort()
    {
        this.viewport = document.createElement( "div" );
        this.viewport.className = "TriNavViewport";
        this.thumbnail.window.appendChild( this.viewport );
        this.viewport.addEventListener( "mouseover", ( evt ) => { evt.currentTarget.style.cursor = "move"; } );
        this.viewport.addEventListener( "mouseout", ( evt ) => { evt.currentTarget.style.cursor = "inherit"; } );
        this.viewport.addEventListener( 'dblclick', ( evt ) => this.handleZoomToFit( evt ), true );

        this.hammer = new Hammer.Manager( this.viewport, {
          recognizers: [ [ Hammer.Pan ] ],
        } );

        this.hammer.on( "panstart", this.handleStartViewportDrag.bind( this ) );
        this.hammer.on( "panmove", this.handleViewportDrag.bind( this ) );
    }

    /*
     * Get reference 0,0 points from which to measure viewport offset
     */
    initThumbnail()
    {
        if (!this.shadow.SVG ) return;


        // Find the 0,0 point of the client div in SVG coordinates
        const pt = this.shadow.SVG.createSVGPoint();
        let ctm = this.shadow.SVG.getCTM();
        // Firefox returns null for getCTM on the root element.  The first element
        // should be a layer and be the same transform
        if( ctm === null )
        {
            ctm = this.shadow.SVG.firstElementChild.getCTM();
        }
        this.initSVG_0_0 = pt.matrixTransform( ctm.inverse() );
        ctm = this.shadow.SVG.getScreenCTM();
        pt.x = this.initSVG_0_0.x;
        pt.y = this.initSVG_0_0.y;
        this.initClient_0_0 = pt.matrixTransform( ctm );

        this.sizeAndPositionViewport( this.triFloorPlan.svgViewBox );
    }

    /*
     * Size the tumbnail window on start and if the client window is resized.
     */
    sizeNavWindow()
    {
        const { triFloorPlan, enabled, floorPlanLoaded } = this.props;
        const { svgElement } = triFloorPlan;
        if( !this.thumbnail.window ) return;

        if (!floorPlanLoaded || !enabled || !svgElement) return null;

        if (!this.thumbnail.SVG) return;

        TriFloorPlanUtils.setSvgViewBox(this.thumbnail.SVG, 0, 0, 0, 0);
        const svgExtents = TriFloorPlanUtils.getSvgExtents(this.thumbnail.SVG);
        const svgViewPort = this.thumbnail.SVG.getBoundingClientRect();
        const svgViewBox = TriFloorPlanUtils.createInitialSvgViewBox(
            svgExtents,
            svgViewPort
        );
        if( !svgViewBox ) return;

        let size = this.size;
        if(    svgElement.clientHeight < this.minHeight + 100 
            || svgElement.clientWidth < this.minWidth + 100 )
        {
            size = 75;
        }

        this.thumbnail.width = size;
        this.thumbnail.height = size;

        if (svgElement.clientHeight > svgElement.clientWidth)
        {
            this.thumbnail.height = Math.round(svgElement.clientHeight * this.thumbnail.width / svgElement.clientWidth );
        }
        else
        {
            this.thumbnail.width = Math.round(svgElement.clientWidth * this.thumbnail.height / svgElement.clientHeight );
        }
        this.thumbnail.Scale = this.thumbnail.height / svgElement.clientHeight;

        this.thumbnail.window.style.width = "" + this.thumbnail.width + "px";
        this.thumbnail.window.style.height = "" + this.thumbnail.height + "px";

        if( this.zoomBox )
        {
            if( this.thumbnail.height >= 100 )
            {
                this.zoomBox.style.visibility = "visible";
            }
            else
            {
                this.zoomBox.style.visibility = "hidden";
            }
        }

        TriFloorPlanUtils.setSvgViewBox(
            this.thumbnail.SVG,
            svgViewBox.x,
            svgViewBox.y,
            svgViewBox.scaleX != 0 ? svgViewPort.width / svgViewBox.scaleX : 0,
            svgViewBox.scaleY != 0 ? svgViewPort.height / svgViewBox.scaleY : 0
        );

        // Keep nav control in the main window
        const container = svgElement.parentElement;
        if( this.navWindow.offsetTop + this.navWindow.offsetHeight > container.offsetHeight )
        {
            this.navWindow.style.top = (container.offsetHeight - this.navWindow.offsetHeight) + "px";
        }
        if( this.navWindow.offsetTop < 0 )
        {
            this.navWindow.style.top = 0;
        }
        if( this.navWindow.offsetLeft + this.navWindow.offsetWidth > container.offsetWidth )
        {
            this.navWindow.style.left = (container.offsetWidth - this.navWindow.offsetWidth) + "px";
        }
        if( this.navWindow.offsetLeft < 0 )
        {
            this.navWindow.style.left = 0;
        }

        if( this.zoom )
        {
            this.zoom.style.width = (this.thumbnail.width - 24) + "px";
        }

        if( svgElement.clientHeight < this.minHeight || svgElement.clientWidth < this.minWidth )
        {
            this.navWindow.style.visibility = "hidden";
        }
        else
        {
            this.navWindow.style.visibility = "visible";
        }
    }

    /*
     * Size the shadow window on start and if the client window is resized.
     */
    sizeShadow()
    {
        const { triFloorPlan, enabled, floorPlanLoaded } = this.props;
        const { svgElement } = triFloorPlan;
        if( !this.thumbnail.window ) return;

        if( !floorPlanLoaded || !enabled || !svgElement ) return null;

        if( !this.shadow.SVG ) return;
 
        const container = svgElement.parentElement;
        this.shadow.window.style.height = container.offsetHeight + "px";
        this.shadow.window.style.width = container.offsetWidth + "px";

        TriFloorPlanUtils.setSvgViewBox( this.shadow.SVG, 0, 0, 0, 0 );
        const svgExtents = TriFloorPlanUtils.getSvgExtents(this.shadow.SVG);
        const svgViewPort = this.shadow.SVG.getBoundingClientRect();
        this.shadow.svgViewBox = TriFloorPlanUtils.createInitialSvgViewBox(
            svgExtents,
            svgViewPort
        );

        let scaleX = this.shadow.svgViewBox.scaleX;
        let scaleY = this.shadow.svgViewBox.scaleY;
        if( scaleX === 0 ) scaleX = 1;
        if( scaleY === 0 ) scaleY = 1;
        TriFloorPlanUtils.setSvgViewBox(
            this.shadow.SVG,
            this.shadow.svgViewBox.x,
            this.shadow.svgViewBox.y,
            svgViewPort.width / scaleX,
            svgViewPort.height / scaleY
        );
    }

    /*
     * Size and place the viewport in the thumbnail window in response to chanages in
     * the SVG scale and position
     * Update the possition of the Zoom slider based on current scale
     */
    sizeAndPositionViewport( svgViewBox )
    {
        if( !svgViewBox ) return;
        if( !this.initSVG_0_0 ) return;
        if( !this.shadow || !this.shadow.svgViewBox ) return;

        const { svgElement } = this.triFloorPlan;

        // preserveAspectRatio is set to xMidYMid.  One of the zoom to fit opperationa
        // may set a viewBox with a different aspect ratio than the viewport.  This
        // may cause one of the scales to be wrong.  Because meet is the defalt behavior
        // the smaller scale is the correct one and the one which should be used.
        const zoomFactorX = this.shadow.svgViewBox.scaleX / svgViewBox.scaleX;
        const zoomFactorY = this.shadow.svgViewBox.scaleY / svgViewBox.scaleY;
        const zoomFactor = zoomFactorX < zoomFactorY ? zoomFactorX : zoomFactorY;

        const width = this.thumbnail.width * zoomFactor;
        const height = this.thumbnail.height * zoomFactor;

        const ctm = svgElement.getScreenCTM();
        const pt = svgElement.createSVGPoint();
        pt.x = this.initSVG_0_0.x;
        pt.y = this.initSVG_0_0.y;
        const deltaLoc = pt.matrixTransform( ctm );
        const deltaX = deltaLoc.x - this.initClient_0_0.x;
        const deltaY = deltaLoc.y - this.initClient_0_0.y;

        const x = deltaX * this.thumbnail.Scale * zoomFactor;
        const y = deltaY * this.thumbnail.Scale * zoomFactor;

        this.viewport.style.width = "" + Math.round( width ) + "px";
        this.viewport.style.height = "" + Math.round( height ) + "px";
        this.viewport.style.left = "" + Math.round( -x ) + "px";
        this.viewport.style.top = "" + Math.round( -y ) + "px";
        this.clipViewport();

        let value = zoomFactor;
        if( value < 1 )
        {
          value = 1 / value + 1 / this.minScale - 1;
        }
        else
        {
          value = 1 / this.minScale - value + 1;
        }

        if( this.zoom ) this.zoom.value = value;
    }

    /*
     * Clip the viewport to the bounds of the thumbnail
     */
    clipViewport()
    {
        const left = this.viewport.offsetLeft > 0 ? 0 : - this.viewport.offsetLeft;
        const top = this.viewport.offsetTop > 0 ? 0 : - this.viewport.offsetTop;
        const width = this.thumbnail.width - this.viewport.offsetLeft;
        const height = this.thumbnail.height - this.viewport.offsetTop;
        const clipBox = "rect(" + top + "px," + width + "px," + height + "px," + left + "px)"

        this.viewport.style.clip = clipBox;
    }

    /*
     * Viewport drag and resize 
     */
    handleStartViewportDrag()
    {
        const { svgElement } = this.triFloorPlan;
        this.dragViewport = {
            start: {
                x: this.viewport.offsetLeft,
                y: this.viewport.offsetTop,
            },
            current: {
                x: 0,
                y: 0,
            }
        };

        const ctm = svgElement.getScreenCTM();
        const matrix = ctm.inverse();
        this.panStart = {
            matrix,
            orig: {
                x: this.props.triFloorPlan.svgViewBox.x,
                y: this.props.triFloorPlan.svgViewBox.y,
            },
            start: this.viewportLocToClientLoc(),
        };
    }

    /*
     * Move drag the viewport with the mouse.  Pan the SVG to keep the thumbnail
     * viewport and client viewport (svgVieBox) aligned
     *
     * Pan is from the current viewport and SVG locations.  There is nothing to
     * correct if the viewport gets off locations.  Dragging the floorplan
     * directly does rest the viewport location
     */
    handleViewportDrag( evt )
    {
        evt.preventDefault();

        // Move the SVG element
        const { svgViewBox, svgElement } = this.triFloorPlan;

        this.viewport.style.cursor = "move";

        // Move the viewport window
        this.dragViewport.current.x = evt.deltaX;
        this.dragViewport.current.y = evt.deltaY;

        // Constrain the pan so an edge of the viewport always shows
        let x = this.dragViewport.start.x + this.dragViewport.current.x
        const width = this.viewport.offsetWidth;
        if( x < 4 - width ) x = 4 - width;  // Left edge
        if( x > this.thumbnail.width - 4 ) x = this.thumbnail.width - 4; // Right edge

        let y = this.dragViewport.start.y + this.dragViewport.current.y
        const height = this.viewport.offsetHeight;
        if( y < 4 - height ) y = 4 - height;  // Top edge
        if( y > this.thumbnail.height - 4 ) y = this.thumbnail.height - 4; // Bottom edge

        this.viewport.style.left = x + 'px';
        this.viewport.style.top = y + 'px';

        this.clipViewport();

        // Pan the floor plan
        const loc = this.viewportLocToClientLoc()
        const deltaX = this.panStart.start.x - loc.x;
        const deltaY = this.panStart.start.y - loc.y;
        this.triFloorPlan.svgViewBox = TriFloorPlanUtils.panSvgViewBox(
            svgElement,
            svgViewBox,
            deltaX,
            deltaY,
            this.panStart
        );
    }

    /*
     * Reset Pan and Zoom to starting position
     */
    handleZoomToFit()
    {
        this.floorPlanDidResize();
        this.triFloorPlan.svgViewBox = this.shadow.svgViewBox;
    }

    viewportLocToClientLoc()
    {
        const { svgViewBox } = this.triFloorPlan;

        // preserveAspectRatio is set to xMidYMid.  One of the zoom to fit opperationa
        // may set a viewBox with a different aspect ratio than the viewport.  This
        // may cause one of the scales to be wrong.  Because meet is the defalt behavior
        // the smaller scale is the correct one and the one which should be used.
        const zoomFactorX = this.shadow.svgViewBox.scaleX / svgViewBox.scaleX;
        const zoomFactorY = this.shadow.svgViewBox.scaleY / svgViewBox.scaleY;
        const zoomFactor = zoomFactorX < zoomFactorY ? zoomFactorX : zoomFactorY;

        const x = this.viewport.offsetLeft / ( this.thumbnail.Scale * zoomFactor );
        const y = this.viewport.offsetTop / ( this.thumbnail.Scale * zoomFactor );

        return {
          x, y
        };
    }

   /*
    * Drag thumbnail to possition on the floorplan
    */
    handleStartDragNav( evt )
    {
        this.isDraggingNav = true;
        this.dragNav = {
            offset:
            {
                x: evt.screenX - this.navWindow.offsetLeft,
                y: evt.screenY - this.navWindow.offsetTop,
            },
            start: {
                x: this.navWindow.offsetLeft,
                y: this.navWindow.offsetTop,
            },
            current: {
                x: this.navWindow.offsetLeft,
                y: this.navWindow.offsetTop,
            }
        };
    }

    handleDragNav( evt )
    {
        if( !evt.buttons || evt.buttons === 0 )
        {
            this.dragNav = {};
            this.isDraggingNav = false;
            return;
        }
        if( !this.isDraggingNav ) return;

        const { triFloorPlan, floorPlanLoaded } = this.props;
        if( !floorPlanLoaded )
        {
            return;
        }

        evt.preventDefault();

        const { svgElement } = triFloorPlan;

        const container = svgElement.parentElement;
        this.dragNav.current.x = evt.screenX - this.dragNav.offset.x;
        this.dragNav.current.y = evt.screenY - this.dragNav.offset.y;

        let left = this.dragNav.current.x;
        if( left < 0 ) left = 0;
        if( left > container.offsetWidth - this.navWindow.offsetWidth )
        {
            left = container.offsetWidth - this.navWindow.offsetWidth;
        }

        let top = this.dragNav.current.y;
        if( top < 0 ) top = 0;
        if( top > container.offsetHeight - this.navWindow.offsetHeight )
        {
            top = container.offsetHeight - this.navWindow.offsetHeight;
        }

        this.navWindow.style.left = "" + left + 'px';
        this.navWindow.style.top = "" + top + 'px';
    }

    /*
     * Reset the Nav window position to lower right corner
     */
    handleNavResetPosition()
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;
        const left = container.offsetWidth - this.navWindow.offsetWidth;
        const top = container.offsetHeight - this.navWindow.offsetHeight;
        this.navWindow.style.left = "" + left + 'px';
        this.navWindow.style.top = "" + top + 'px';
    }

    /*
     * Zoom initiated by zoom-in button, zoom-out button or zoom slider
     */
    handleZoom()
    {
        const { triFloorPlan, floorPlanLoaded } = this.props;
        if( !floorPlanLoaded )
        {
            return;
        }

        const {
            svgElement,
            svgExtents,
        } = triFloorPlan;
        const container = svgElement.parentElement;

        let zoomFactor = this.zoom.value;
        if( zoomFactor - 1 / this.minScale >= 0 )
        {
            zoomFactor = 1 / ( zoomFactor - 1 / this.minScale + 1 ) ;
        }
        else
        {
            zoomFactor = 1 / this.minScale - zoomFactor + 1;
        }
        if( zoomFactor === 0 ) zoomFactor = 1;

        // Use the center of the SVG extent for the zoom center point.  This tends to move
        // the viewport back towards the center of the content.
        //
        // This isn't working perfectly, espically if the floorplan was progamtically
        // re-sized, but it's "good enough"
        const ctm = svgElement.getScreenCTM();
        const pt = svgElement.createSVGPoint();
        pt.x = svgExtents.x + svgExtents.width / 2;
        pt.y = svgExtents.y + svgExtents.height / 2;
        const center = pt.matrixTransform( ctm );

        // Ensure that the center point is within the svgElement. x and y end up NaN
        // if it is not.
        if( center.x > container.offsetWidth ) center.x = container.offsetWidth;
        if( center.y > container.offsetHeight ) center.y = container.offsetHeight;
        if( center.x < 0 ) center.x = 0;
        if( center.y < 0 ) center.y = 0;

        this.applyScale(
            this.shadow.svgViewBox.scaleX / zoomFactor,
            this.shadow.svgViewBox.scaleY / zoomFactor,
            {
              x: center.x,
              y: center.y,
            }
        );
    }
 
    applyScale = throttle( ( newScaleX, newScaleY, center ) =>
    {
        const { triFloorPlan, floorPlanLoaded } = this.props;
        if( !floorPlanLoaded )
        {
            return;
        }

        const {
            svgViewBox,
            svgElement,
            svgViewPort,
        } = triFloorPlan;

        triFloorPlan.svgViewBox = TriFloorPlanUtils.scaleSvgViewBox(
            svgViewBox,
            svgElement,
            this.shadow.svgViewBox,
            svgViewPort,
            this.minScale,
            this.maxScale,
            newScaleX,
            newScaleY,
            center
        );
    }, 10 );

    /*********************************************************************************/
    /* Zoom Box
    /*********************************************************************************/

    // Button event to toggle on and off
    handleZoomBox()
    {
        const { triFloorPlan } = this.props;
        const { svgElement } = triFloorPlan;
        const container = svgElement.parentElement;

        if( this.isZoomBoxActive || this.dragBoundingBox )
        {
            this.removeZoomBoxListeners( container );
            this.dragBoundingBox = null;
            return;
        }

        triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === TriPanPlugin.type ) instance.enabled = false;
        } );

        this.zoomBox.className = "TriNavControlActive";
        this.zoomBoxImg.src = zoomBoxIconSelected;
        this.isZoomBoxActive = true;
        container.addEventListener( 'mousedown', this.handleDragStart, true );
        container.addEventListener( 'mousemove', this.handleDragMove, true );
        container.addEventListener( 'mouseup', this.handleZoomBoxDragEnd, true );
    }


    // Mouse down event
    handleDragStart( evt )
    {
        if( evt.srcElement === this.zoomBoxImg )
        {
            return;
        }

        super.handleDragStart( evt );
    }

    // Mouse up
    handleZoomBoxDragEnd( evt )
    {
        if( evt.srcElement === this.zoomBoxImg )
        {
            return;
        }

        const { triFloorPlan } = this.props;
        const { svgElement, svgViewPort } = triFloorPlan;
        const container = svgElement.parentElement;

        if( this.dragBox )
        {
            container.removeChild( this.dragBox );
            this.dragBox = null;
        }

        evt.preventDefault();
        this.removeZoomBoxListeners( container );

        if( !this.dragBoundingBox ) return;

        // Normailze top and bottom corners
        let top, left, bottom, right;
        if( this.dragBoundingBox.current.x > this.dragBoundingBox.start.x )
        {
            left = this.dragBoundingBox.start.x;
            right = this.dragBoundingBox.current.x;
        } else {
            right = this.dragBoundingBox.start.x;
            left = this.dragBoundingBox.current.x;
        }

        if( this.dragBoundingBox.current.y > this.dragBoundingBox.start.y )
        {
            top = this.dragBoundingBox.start.y;
            bottom = this.dragBoundingBox.current.y;
        } else {
            bottom = this.dragBoundingBox.start.y;
            top = this.dragBoundingBox.current.y;
        }

        // Normalize the aspect ratio so the selection is centered.
        let height = bottom - top;
        let width = right - left;
        const scaleX = container.scrollWidth / width;
        const scaleY = container.scrollHeight / height;

        if( scaleX > scaleY )
        {
            const newWidth = Math.round( container.scrollWidth / scaleY );
            left = left + width / 2 - newWidth / 2;
            right = left + newWidth;
        }
        else
        {
            const newHeight = Math.round( container.scrollHeight / scaleX );
            top = top + height / 2 - newHeight / 2;
            bottom = top + newHeight;
        }

        // Convert from DOM to svg coordinates
        let matrix = svgElement.getScreenCTM().inverse();
        const topLeft = svgElement.createSVGPoint();
        topLeft.x = svgViewPort.left + left;
        topLeft.y = svgViewPort.top + top;
        const svgTopLeft = topLeft.matrixTransform( matrix );

        const bottomRight = svgElement.createSVGPoint();
        bottomRight.x = svgViewPort.left + right;
        bottomRight.y = svgViewPort.top + bottom;
        const svgBottomRight = bottomRight.matrixTransform( matrix );

        width = svgBottomRight.x - svgTopLeft.x;
        height = svgBottomRight.y - svgTopLeft.y;

        this.zoomToBox( svgTopLeft.x, svgTopLeft.y, width, height );

        this.dragBoundingBox = null;
    }

    removeZoomBoxListeners( container )
    {
        const { triFloorPlan } = this.props;
        triFloorPlan.pluginInstances.forEach( ( instance ) =>
        {
            if( instance.props.type === TriPanPlugin.type ) instance.enabled = true;
        } );

        this.zoomBox.className = "TriNavControl";
        this.zoomBoxImg.src = zoomBoxIcon;
        this.isZoomBoxActive = false;
        container.removeEventListener( 'mousedown', this.handleDragStart, true );
        container.removeEventListener( 'mousemove', this.handleDragMove, true );
        container.removeEventListener( 'mouseup', this.handleZoomBoxDragEnd, true );
    }
}
